import * as React from 'react';
import { theme } from '@Guidelines/Theme/theme';
import useUserPermissions from '@UI/Providers/PermissionProvider/useUserPermissions';
import type { NavigationSection } from '@UI/Routing/navigationSection';
import { css } from '@emotion/react';
import { Navigate } from 'react-router-dom';
import { Message, Spinner } from '@verclam/ui-kit';

const spinnerContainerStyle = css`
	display: flex;
	justify-content: center;
	padding: ${theme.layout.gutter};
`;

const messageContainerStyle = css`
	padding: ${theme.layout.gutter};
`;

type Props = {
	readonly sections: readonly NavigationSection<string>[];
	readonly emptyMessage: string;
};

/**
 * Resolver for a section's entry point (e.g. `/administration`, `/facilities`): not a page guard
 * (`RequirePermission` already covers each screen), but the destination picker for a path that names
 * no screen of its own. Redirects to the first entry of `sections` the user can access, so the entry
 * point and the route guards can never disagree. Shared by every section — one catalogue in, one
 * destination out — rather than duplicated per feature.
 */
const SectionLanding: React.FC<Props> = ({ sections, emptyMessage }) => {
	const { isResolved, hasPermission } = useUserPermissions();

	const destination = React.useMemo(
		() =>
			sections.find((section) => {
				if (!isResolved) {
					return null;
				}

				return hasPermission(section.permission);
			}),
		[sections, hasPermission, isResolved],
	);

	if (!isResolved) {
		return (
			<div css={spinnerContainerStyle}>
				<Spinner aria-label="Vérification des droits d'accès" />
			</div>
		);
	}

	if (!destination) {
		return (
			<div css={messageContainerStyle}>
				<Message severity="error" text={emptyMessage} />
			</div>
		);
	}

	return <Navigate to={destination.path} replace />;
};

export default SectionLanding;
